import { TextInput } from "@/components/atoms/TextInput";
import { Typography } from "@/components/atoms/Typography";
import { CountryCodeDropdown } from "@/components/molecules/CountryCodeDropdown";
import { useTranslation } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { COUNTRIES, type Country } from "@/core/utils/countryCodes";
import React from "react";
import { StyleSheet, View } from "react-native";

export interface PhoneInputRowProps {
  selectedCountry?: Country | any;
  phoneNumber: string;
  onCountrySelect: (country: Country | any) => void;
  onPhoneChange: (value: string) => void;
  onPhoneBlur?: () => void;
  disabled?: boolean;
  error?: string;
  label?: string;
  placeholder?: string;
  [key: string]: any;
}

export function PhoneInputRow({
  selectedCountry,
  phoneNumber,
  onCountrySelect,
  onPhoneChange,
  onPhoneBlur,
  disabled = false,
  error,
  label,
  placeholder,
}: PhoneInputRowProps) {
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const effectiveCountry =
    selectedCountry && (selectedCountry as Country).code
      ? selectedCountry
      : COUNTRIES[0];

  return (
    <View style={styles.container}>
      {label ? (
        <Typography
          variant="caption"
          color="tertiary"
          style={[styles.label, { textAlign: isRTL ? "right" : "left" }]}
        >
          {label}
        </Typography>
      ) : null}
      <View style={styles.row}>
        <CountryCodeDropdown
          selectedCountry={effectiveCountry}
          onCountrySelect={onCountrySelect}
          disabled={disabled}
          containerStyle={styles.countryDropdown}
        />
        <TextInput
          value={phoneNumber}
          placeholder={placeholder || t("support.phonePlaceholder" as any)}
          onChangeText={onPhoneChange}
          keyboardType="phone-pad"
          editable={!disabled}
          containerStyle={styles.phoneInput}
          onBlur={onPhoneBlur}
        />
      </View>
      {!!error && (
        <Typography variant="footnote" color="error" style={{ marginTop: 4 }}>
          {error}
        </Typography>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: 4 },
  label: { paddingHorizontal: 8 },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "nowrap",
  },
  countryDropdown: {
    minWidth: 128,
    maxWidth: 168,
    flexShrink: 0,
  },
  phoneInput: { flex: 1, minWidth: 0 },
});
